/**
 * WorkstreamStore — the workstreams feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY workstreams module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<Workstream>('workstreams')` + `api.identityProvider`). It
 * never imports ApiClient or calls fetch. React hooks call this store; pages
 * call the hook.
 *
 * Persistence model: each workstream is one document with id `ws_<uuid>` in the
 * real 'workstreams' generic-store collection (DDB pointer row + S3 body). The
 * board-renderable fields (engineer, title, currentStage, systemsAffected,
 * followers, sprintStoryCount, goalId, poc, createdBy, lastUpdated, deleted)
 * are mirrored onto the DDB pointer row's index attributes so the board groups
 * + renders directly from the pointer rows — WITHOUT hydrating S3 bodies per
 * workstream. The full narrative fields + id arrays live in the S3 body,
 * hydrated only on the detail view via {@link get}.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type { Workstream, WorkstreamDraft, WorkstreamSummary } from './types';
import { isFollowing, workstreamDocId } from './workstreamModel';

export const WORKSTREAMS_COLLECTION = 'workstreams';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  engineer: 'engineer',
  title: 'title',
  currentStage: 'currentStage',
  systemsAffected: 'systemsAffected',
  followers: 'followers',
  sprintStoryCount: 'sprintStoryCount',
  goalId: 'goalId',
  poc: 'poc',
  createdBy: 'createdBy',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

export class WorkstreamStore {
  private readonly docs: DocumentStore<Workstream>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Workstream>(WORKSTREAMS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(ws: Workstream): StringMap {
    return {
      [ROW.engineer]: ws.engineer ?? '',
      [ROW.title]: ws.title ?? '',
      [ROW.currentStage]: ws.currentStage ?? '',
      [ROW.systemsAffected]: JSON.stringify(ws.systemsAffected ?? []),
      [ROW.followers]: JSON.stringify(ws.followers ?? []),
      [ROW.sprintStoryCount]: String((ws.sprintStoryIds ?? []).length),
      [ROW.goalId]: ws.goalId ?? '',
      [ROW.poc]: ws.poc ?? '',
      [ROW.createdBy]: ws.createdBy ?? '',
      [ROW.lastUpdated]: ws.lastUpdated ?? '',
      [ROW.deleted]: ws.deleted ? 'true' : 'false',
    };
  }

  /** Safely parses a JSON-encoded string-array attribute. */
  private parseListAttr(raw: string | undefined): string[] {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter((t) => typeof t === 'string')
        : [];
    } catch {
      return [];
    }
  }

  /** Projects a raw pointer row into a lightweight summary. */
  private toSummary(row: StringMap): WorkstreamSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      engineer: row[ROW.engineer] ?? '',
      title: row[ROW.title] ?? '',
      currentStage: row[ROW.currentStage] ?? '',
      systemsAffected: this.parseListAttr(row[ROW.systemsAffected]),
      followers: this.parseListAttr(row[ROW.followers]),
      sprintStoryCount: Number(row[ROW.sprintStoryCount] ?? '0') || 0,
      goalId: row[ROW.goalId] || undefined,
      poc: row[ROW.poc] ?? '',
      createdBy: row[ROW.createdBy] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /**
   * Lists all non-deleted workstream summaries. Filters to this collection
   * defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<WorkstreamSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === WORKSTREAMS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single workstream document by id (hydrates the S3 body). */
  async get(id: string): Promise<Workstream | undefined> {
    return this.docs.get(id);
  }

  /**
   * Creates a new workstream. Seeds id, createdBy (from identity), an empty
   * followers list and lastUpdated, and returns the saved document.
   */
  async create(draft: WorkstreamDraft): Promise<Workstream> {
    const identity = await this.api.identityProvider.getIdentity();
    const ws = this.fromDraft(workstreamDocId(), draft, identity.alias, []);
    await this.docs.save(ws.id, ws, this.indexAttributes(ws));
    return ws;
  }

  /**
   * Applies a draft's fields to an existing workstream in place (same id/
   * createdBy/followers), refreshes lastUpdated, and re-mirrors index
   * attributes.
   */
  async applyDraft(ws: Workstream, draft: WorkstreamDraft): Promise<Workstream> {
    const updated = this.fromDraft(ws.id, draft, ws.createdBy, ws.followers ?? []);
    return this.update(updated);
  }

  /** Persists a full workstream document in place. */
  async update(ws: Workstream): Promise<Workstream> {
    const updated: Workstream = {
      ...ws,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /** Moves a workstream to a new stage. No-op-safe. */
  async setStage(ws: Workstream, stage: string): Promise<Workstream> {
    if (ws.currentStage === stage) return ws;
    return this.update({ ...ws, currentStage: stage });
  }

  /**
   * Toggles the current user (from identity) in a workstream's followers list.
   * Returns the updated document. Hydrates the doc first so both the S3 body
   * and the pointer row's mirrored `followers` stay in sync.
   */
  async toggleFollow(id: string): Promise<Workstream | undefined> {
    const existing = await this.docs.get(id);
    if (!existing) return undefined;
    const identity = await this.api.identityProvider.getIdentity();
    const alias = identity.alias;
    const followers = existing.followers ?? [];
    const following = isFollowing(followers, alias);
    const nextFollowers = following
      ? followers.filter((f) => f.trim().toLowerCase() !== alias.trim().toLowerCase())
      : [...followers, alias];
    return this.update({ ...existing, followers: nextFollowers });
  }

  /**
   * Soft-deletes a workstream: fetches it, flags it deleted, and updates both
   * the S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Workstream = {
      ...existing,
      deleted: true,
      lastUpdated: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  /** Builds a full Workstream from a draft, seeding id/createdBy/followers. */
  private fromDraft(
    id: string,
    draft: WorkstreamDraft,
    createdBy: string,
    followers: string[],
  ): Workstream {
    return {
      id,
      engineer: draft.engineer.trim(),
      title: draft.title.trim(),
      why: draft.why ?? '',
      whatChanging: draft.whatChanging ?? '',
      systemsAffected: draft.systemsAffected ?? [],
      technicalApproach: draft.technicalApproach ?? '',
      expectedImpact: draft.expectedImpact ?? [],
      currentStage: draft.currentStage,
      sprintStoryIds: draft.sprintStoryIds ?? [],
      goalId: draft.goalId || undefined,
      dependencies: draft.dependencies ?? [],
      poc: draft.poc?.trim() ?? '',
      followers,
      createdBy,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
  }
}
